import { Suspense, useEffect, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import ApartmentIcon from '@mui/icons-material/Apartment'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useCondominium } from '../contexts/CondominioContext'
import { useAuth } from '../contexts/AuthContext'
import { useTenantModulos } from '../hooks/useTenantModulos'
import { fetchAllPages } from '../utils/fetchAllPages'
import type { EstateResponse, SubscricaoModulo } from '../utils/types'

// 1.º segmento de /properties/:id/<seg>/… → módulo da subscrição que o cobre.
// Segmentos de gestão de base (buildings, blocks, units, residents, users) e o
// dashboard não constam aqui — são sempre acessíveis.
const SEGMENTO_MODULO: Record<string, SubscricaoModulo> = {
  finance: 'FINANCAS',
  'access-control': 'CONTROLO_ACESSO',
  'common-areas': 'AREAS_COMUNS',
  parking: 'ESTACIONAMENTO',
  maintenance: 'MANUTENCAO',
  arrendamentos: 'ARRENDAMENTO',
  communication: 'COMUNICACAO',
  documents: 'COMUNICACAO',
  assembleias: 'ASSEMBLEIAS',
}

export default function CondominiumLayout() {
  const { t } = useTranslation('condominiums')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { temModulo } = useTenantModulos()
  const { activeCondominium, selectCondominium, clearActiveCondominium } = useCondominium()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const { user, switchEstate, exitEstate, hasPermission } = useAuth()
  // Exit (largar o estate activo, ficar em vista global) mantém-se exclusivo do ADMIN.
  const isAdmin = hasPermission('ESTATE_CONTEXT_EXIT')
  // Mudar entre estates é agora permitido a qualquer papel — mas só faz
  // sentido mostrar o selector a quem está ligado a mais do que um.
  const canSwitchEstates = (user?.estateIds?.length ?? 0) > 1
  const showContextControl = canSwitchEstates || isAdmin

  // Dropdown "Mudar" — troca directa de condomínio sem passar pela lista.
  const [switchAnchorEl, setSwitchAnchorEl] = useState<null | HTMLElement>(null)
  const switchMenuOpen = Boolean(switchAnchorEl)
  // `null` = ainda por carregar. Vem de /api/estates (a mesma fonte do
  // /properties): o ADMIN recebe todos os estates do tenant e os restantes
  // papéis só os seus. /api/condominios não serve — para não-ADMIN devolve só
  // o condomínio activo.
  const [switchOptions, setSwitchOptions] = useState<EstateResponse[] | null>(null)
  const [switchOptionsFailed, setSwitchOptionsFailed] = useState(false)

  const handleOpenSwitchMenu = (event: MouseEvent<HTMLElement>) => {
    setSwitchAnchorEl(event.currentTarget)
    setSwitchOptionsFailed(false)
    // Recarrega a cada abertura (a lista antiga mantém-se visível entretanto)
    // para apanhar condomínios criados/desactivados depois do primeiro carregamento.
    fetchAllPages<EstateResponse>('/api/estates', { suppressForbiddenToast: true })
      .then((estates) =>
        // switch-estate só aceita estates activos, e este módulo só gere condomínios.
        setSwitchOptions(estates.filter((e) => e.stateType === 'CONDOMINIUM' && e.status === 'ACTIVE')),
      )
      .catch(() => setSwitchOptionsFailed(true))
  }

  const handleCloseSwitchMenu = () => setSwitchAnchorEl(null)

  const handleSelectCondominium = async (targetId: string) => {
    handleCloseSwitchMenu()
    if (targetId === activeCondominium?.id) return
    await switchEstate(targetId)
    navigate(`/properties/${targetId}`)
  }

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setIsLoading(true)
    setError('')

    selectCondominium(id)
      .then(() => {
        if (canSwitchEstates) return switchEstate(id)
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar este condomínio.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id, selectCondominium, canSwitchEstates])

  const handleSwitch = async () => {
    clearActiveCondominium()
    if (isAdmin) await exitEstate()  // ← remove o estate activo do token JWT; exit é exclusivo do ADMIN
    navigate('/properties')
  }

  const handleExitToList = () => {
    handleCloseSwitchMenu()
    handleSwitch()
  }

  // Rota de um módulo que o plano do tenant não inclui — o item do menu já
  // desaparece (ver Sidebar), isto trava o acesso por link directo.
  const base = `/properties/${id}`
  const segmento = location.pathname.startsWith(`${base}/`)
    ? location.pathname.slice(base.length + 1).split('/')[0]
    : ''
  const moduloDaRota = SEGMENTO_MODULO[segmento]
  const moduloForaDoPlano = moduloDaRota != null && !temModulo(moduloDaRota)
  // Arrendamento é 100% self-service (permissões *_PROPRIO, resolvidas pela
  // entidade do próprio utilizador). Sem entidade associada não há nada
  // "próprio" — trava o acesso directo por URL, tal como o item do menu já some.
  const exigeEntidade = segmento === 'arrendamentos' && !user?.temEntidade
  const rotaBloqueada = moduloForaDoPlano || exigeEntidade
  const mensagemBloqueio = moduloForaDoPlano
    ? 'Este módulo não está incluído no plano da empresa. Contacte o suporte para o activar.'
    : 'Este módulo é exclusivo dos condóminos. A sua conta não está associada a nenhuma unidade neste condomínio.'

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !activeCondominium) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error || 'Condomínio não encontrado.'}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={handleSwitch} startIcon={<SwapHorizIcon />}>
          Voltar à lista de condomínios
        </Button>
      </Container>
    )
  }

  return (
    <Box>
      {/* Barra de contexto activo */}
      <Box
        sx={{
          bgcolor: '#EEF2FF',
          borderBottom: '1px solid #E0E7FF',
          px: { xs: 2, sm: 3 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <ApartmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="body2"  color="primary.dark">
          {activeCondominium.nome}
        </Typography>
        <Chip
          label={activeCondominium.activo ? 'Activo' : 'Inactivo'}
          size="small"
          color={activeCondominium.activo ? 'success' : 'default'}
          sx={{ height: 20, fontSize: '0.6875rem' }}
        />

        <Box sx={{ flexGrow: 1 }} />

        {showContextControl && (
          <>
            <Button
              size="small"
              variant="text"
              startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenSwitchMenu}
              aria-haspopup="menu"
              aria-expanded={switchMenuOpen}
            >
              {t('context.switch')}
            </Button>

            <Menu
              anchorEl={switchAnchorEl}
              open={switchMenuOpen}
              onClose={handleCloseSwitchMenu}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              {switchOptions === null ? (
                <MenuItem disabled sx={{ gap: 1 }}>
                  {switchOptionsFailed ? (
                    'Não foi possível carregar os condomínios.'
                  ) : (
                    <>
                      <CircularProgress size={16} /> A carregar…
                    </>
                  )}
                </MenuItem>
              ) : (
                switchOptions.map((c) => (
                  <MenuItem
                    key={c.id}
                    selected={c.id === activeCondominium.id}
                    onClick={() => handleSelectCondominium(c.id)}
                  >
                    <ListItemText
                      primary={c.name}
                      secondary={c.id === activeCondominium.id ? 'Actual' : undefined}
                    />
                  </MenuItem>
                ))
              )}

              {/* "Sair" para a vista global — exclusivo do ADMIN */}
              {isAdmin && (
                <>
                  <Divider />
                  <MenuItem onClick={handleExitToList}>
                    <ListItemText primary={t('context.viewAll')} />
                  </MenuItem>
                </>
              )}
            </Menu>
          </>
        )}
      </Box>

      {/* Conteúdo específico do condomínio activo (rotas filhas) */}
      {rotaBloqueada ? (
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {mensagemBloqueio}
          </Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate(base)} startIcon={<SwapHorizIcon />}>
            Voltar ao painel
          </Button>
        </Container>
      ) : (
        // Suspense local: enquanto o chunk da página filha (lazy) carrega, a
        // sidebar e a barra de contexto ficam de pé — só a área de conteúdo gira.
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      )}
    </Box>
  )
}